import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { API_BASE_URL, API_ENDPOINTS } from "../api/config";

// ─── Constants ───────────────────────────────────────────────────────────────

const CONSENT_VERSION = "1.0";

export const COOKIE_CATEGORIES = {
  essential:   { label: "Essential",   default: true,  locked: true,  showInBanner: true  },
  analytics:   { label: "Analytics",   default: false, locked: false, showInBanner: true  },
  advertising: { label: "Advertising", default: false, locked: false, showInBanner: false },
  functional:  { label: "Functional",  default: false, locked: false, showInBanner: false },
};

const CONSENT_SCRIPTS = [
  { category: "analytics", src: "https://www.googletagmanager.com/gtag/js?id=G-6EPST02978" },
];

// ─── Cookie storage ───────────────────────────────────────────────────────────

const COOKIE_NAME = "rg_consent";
const COOKIE_MAX_AGE_DAYS = 180;

const setCookie = (name, value, days) => {
  const expires = new Date();
  expires.setDate(expires.getDate() + days);
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax${secure}`;
};

const getCookie = (name) => {
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.split("=")[1]) : null;
};

const deleteCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

const generateSessionId = () => {
  try {
    const existing = getCookie("rg_session_id");
    if (existing) return existing;
    const id = crypto.randomUUID();
    setCookie("rg_session_id", id, 365);
    return id;
  } catch {
    return "unknown";
  }
};

const postConsentToAPI = (body) => {
  fetch(`${API_BASE_URL}${API_ENDPOINTS.COOKIE_CONSENT}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  })
    .then((res) => {
      if (!res.ok) {
        return res.text().then((text) =>
          console.error("[CONSENT] API returned error:", res.status, text)
        );
      }
    })
    .catch((err) => console.error("[CONSENT] API request failed:", err.message));
};

const getConsent = () => {
  try {
    const raw = getCookie(COOKIE_NAME);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const setConsent = (data) => {
  try {
    setCookie(COOKIE_NAME, JSON.stringify(data), COOKIE_MAX_AGE_DAYS);
    postConsentToAPI(data);
  } catch (err) {
    console.error("[CONSENT] Failed to set consent cookie:", err.message);
  }
};

const clearConsent = () => {
  try {
    deleteCookie(COOKIE_NAME);
  } catch {
    // ignore
  }
};

const isConsentFresh = (maxAgeDays = 180) => {
  const consent = getConsent();
  if (!consent?.timestamp || consent?.version !== CONSENT_VERSION) return false;
  const ageMs = Date.now() - new Date(consent.timestamp).getTime();
  return ageMs < maxAgeDays * 24 * 60 * 60 * 1000;
};

// ─── Script gate ──────────────────────────────────────────────────────────────

const injectedSrcs = new Set();

const blockAll = () => {};

const unblock = (category) => {
  CONSENT_SCRIPTS
    .filter((s) => s.category === category)
    .forEach(({ src, category: cat }) => {
      if (!src.startsWith("http://") && !src.startsWith("https://")) return;
      if (injectedSrcs.has(src)) return;

      const el = document.createElement("script");
      el.src = src;
      el.async = true;
      el.dataset.consentCategory = cat;
      document.head.appendChild(el);
      injectedSrcs.add(src);

      if (src.includes("googletagmanager.com/gtag")) {
        const measurementId = new URL(src).searchParams.get("id");
        const initScript = document.createElement("script");
        initScript.dataset.consentCategory = cat;
        initScript.innerHTML = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${measurementId}', { anonymize_ip: true });
        `;
        document.head.appendChild(initScript);
      }
    });
};

const revokeAll = () => {
  document
    .querySelectorAll("script[data-consent-category]")
    .forEach((el) => {
      injectedSrcs.delete(el.src);
      el.remove();
    });

  if (window.dataLayer) window.dataLayer = [];
  if (window.gtag)     window.gtag = undefined;
};

// ─── Jurisdiction detection ───────────────────────────────────────────────────

const detectJurisdiction = async () => {
  try {
    const res = await fetch(`${API_BASE_URL}${API_ENDPOINTS.CONSENT_LAW}`, {
      method: "GET",
      credentials: "include",
    });
    const data = await res.json();
    return data.jurisdiction || "default";
  } catch {
    return "default";
  }
};

// ─── Context ──────────────────────────────────────────────────────────────────

export const ConsentContext = createContext(null);

const buildDefaultCategories = (jurisdiction = "default") =>
  Object.fromEntries(
    Object.entries(COOKIE_CATEGORIES).map(([k, def]) => {
      if (k === "essential") return [k, true];
      if (k === "analytics") return [k, jurisdiction !== "gdpr"];
      return [k, false];
    })
  );

const applyConsent = (categories) => {
  Object.entries(categories).forEach(([cat, granted]) => {
    if (cat === "essential") return;
    if (granted) unblock(cat);
  });
};

const initConsentState = () => {
  const stored = getConsent();
  if (stored && isConsentFresh()) return stored.categories;

  // If either consent cookie exists, user already gave consent —
  // suppress the banner without re-asking.
  const hasConsent = getCookie("rg_consent") || getCookie("rg_consent_server");
  if (hasConsent) return {};

  return null;
};

export const ConsentProvider = ({ children }) => {
  const [consentState, setConsentState] = useState(initConsentState);
  const [isPanelOpen, setIsPanelOpen]   = useState(false);

  const gpcDetected  = useMemo(() => !!navigator.globalPrivacyControl, []);
  const [jurisdiction, setJurisdiction] = useState("default");

  useEffect(() => {
    const init = async () => {
      const detectedJurisdiction = await detectJurisdiction();
      setJurisdiction(detectedJurisdiction);

      const stored = getConsent();
      const fresh  = !!(stored && isConsentFresh());

      if (fresh) {
        applyConsent(stored.categories);
        return;
      }

      if (gpcDetected) {
        const cats = buildDefaultCategories("gdpr");
        setConsent({
          version:      CONSENT_VERSION,
          timestamp:    new Date().toISOString(),
          jurisdiction: detectedJurisdiction,
          gpc_detected: true,
          session_id:   generateSessionId(),
          categories:   cats,
        });
        setConsentState(cats);
        return;
      }

      if (detectedJurisdiction === "gdpr") {
        blockAll();
        return;
      }

      // CCPA / default -- load analytics scripts immediately but keep banner visible
      // consentState stays null so banner remains visible until user makes a choice
      const cats = buildDefaultCategories(detectedJurisdiction);
      applyConsent(cats);
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateConsent = useCallback(
    (cats) => {
      const record = {
        version:      CONSENT_VERSION,
        timestamp:    new Date().toISOString(),
        jurisdiction,
        gpc_detected: gpcDetected,
        session_id:   generateSessionId(),
        categories:   { ...cats, essential: true },
      };
      setConsent(record);
      setConsentState(record.categories);
      revokeAll();
      applyConsent(record.categories);
    },
    [jurisdiction, gpcDetected]
  );

  const resetConsent = useCallback(() => {
    clearConsent();
    revokeAll();
    setConsentState(null);
  }, []);

  const value = useMemo(
    () => ({
      consentState,
      updateConsent,
      resetConsent,
      jurisdiction,
      gpcDetected,
      isPanelOpen,
      setIsPanelOpen,
      defaultCategories: buildDefaultCategories(jurisdiction),
    }),
    [consentState, updateConsent, resetConsent, jurisdiction, gpcDetected, isPanelOpen]
  );

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
};
